import {
  SCORE_START_EVENT,
  SCORE_STOP_EVENT,
  SESSION_SCORE_KEY_PREFIX,
  SESSION_SCORE_TIMESTAMP_KEY_PREFIX,
  REPLY_TRACKING_START_EVENT,
  REPLY_TRACKING_STOP_EVENT,
  REPLY_COUNT_INCREASED_EVENT,
  SESSION_REPLY_COUNT_KEY_PREFIX,
  SESSION_REPLY_TIMESTAMP_KEY_PREFIX,
} from './commentScoreConstants'

type Listener = (channelId: string) => void

let startListeners: Listener[] = []
let stopListeners: Listener[] = []

/**
 * Generates stable localStorage keys for score tracking session
 * For multiple channels, uses 'all' to track the summary score
 * For a single channel, uses the channel ID
 */
export const getScoreSessionKeys = (
  channelIds: string[],
): {
  scoreKey: string
  timestampKey: string
} => {
  let sessionKey: string
  if (channelIds.length > 1) {
    // Use 'all' for multiple channels to track summary score
    sessionKey = 'all'
  } else if (channelIds.length === 1) {
    sessionKey = channelIds[0]
  } else {
    sessionKey = 'unknown'
  }
  return {
    scoreKey: `${SESSION_SCORE_KEY_PREFIX}_${sessionKey}`,
    timestampKey: `${SESSION_SCORE_TIMESTAMP_KEY_PREFIX}_${sessionKey}`,
  }
}

/**
 * Generates stable localStorage keys for reply tracking session
 * For multiple channels, uses 'all' to track the summary reply count
 * For a single channel, uses the channel ID
 */
export const getReplySessionKeys = (
  channelIds: string[],
): {
  replyCountKey: string
  timestampKey: string
} => {
  let sessionKey: string
  if (channelIds.length > 1) {
    // Use 'all' for multiple channels to track summary reply count
    sessionKey = 'all'
  } else if (channelIds.length === 1) {
    sessionKey = channelIds[0]
  } else {
    sessionKey = 'unknown'
  }
  return {
    replyCountKey: `${SESSION_REPLY_COUNT_KEY_PREFIX}_${sessionKey}`,
    timestampKey: `${SESSION_REPLY_TIMESTAMP_KEY_PREFIX}_${sessionKey}`,
  }
}

/**
 * Subscribes to comment score tracking events for managing score-based engagement sessions.
 *
 * This function allows components to listen for score tracking lifecycle events across different
 * channels. It's used by the score tracking system to coordinate session management when users
 * engage with comments and their score changes.
 *
 * @param options - Event handlers for score tracking events
 * @param options.onStart - Called when score tracking starts for a channel
 * @param options.onStop - Called when score tracking stops for a channel
 *
 * @returns Cleanup function to remove all event listeners and subscriptions
 *
 * @example
 * ```typescript
 * const unsubscribe = subscribeToScoreEvents({
 *   onStart: (channelId) => {
 *     console.log(`Score tracking started for channel: ${channelId}`)
 *     startScoreSession()
 *   },
 *   onStop: (channelId) => {
 *     console.log(`Score tracking stopped for channel: ${channelId}`)
 *     stopScoreSession()
 *   }
 * })
 *
 * // Clean up when component unmounts
 * return unsubscribe
 * ```
 */
export const subscribeToScoreEvents = ({
  onStart,
  onStop,
}: {
  onStart?: Listener
  onStop?: Listener
}): (() => void) => {
  if (onStart) startListeners.push(onStart)
  if (onStop) stopListeners.push(onStop)

  const handleEvent = (e: Event): void => {
    const detail = e instanceof CustomEvent ? e.detail : undefined
    const channelId = typeof detail === 'string' ? detail : ''
    if (e.type === SCORE_START_EVENT && onStart) onStart(channelId)
    if (e.type === SCORE_STOP_EVENT && onStop) onStop(channelId)
  }

  window.addEventListener(SCORE_START_EVENT, handleEvent)
  window.addEventListener(SCORE_STOP_EVENT, handleEvent)

  return () => {
    startListeners = startListeners.filter((fn) => fn !== onStart)
    stopListeners = stopListeners.filter((fn) => fn !== onStop)
    window.removeEventListener(SCORE_START_EVENT, handleEvent)
    window.removeEventListener(SCORE_STOP_EVENT, handleEvent)
  }
}

export const triggerEngagementScoreStart = (channelId: string): void => {
  window.dispatchEvent(
    new CustomEvent(SCORE_START_EVENT, { detail: channelId }),
  )
}

export const triggerEngagementScoreStop = (channelId: string): void => {
  window.dispatchEvent(new CustomEvent(SCORE_STOP_EVENT, { detail: channelId }))
}

/**
 * Subscribes to comment reply tracking events for managing reply-based engagement sessions.
 *
 * This function enables components to listen for reply tracking events, including session
 * lifecycle and individual reply increments. It's used to track when users reply to comments
 * and trigger the "RepliedToManyComments" empty state when the threshold is reached.
 *
 * @param options - Event handlers for reply tracking events
 * @param options.onStart - Called when reply tracking starts for a channel
 * @param options.onStop - Called when reply tracking stops for a channel
 * @param options.onReplyCountIncreased - Called each time a user replies to a comment in the channel
 *
 * @returns Cleanup function to remove all event listeners and subscriptions
 *
 * @example
 * ```typescript
 * const unsubscribe = subscribeToReplyEvents({
 *   onStart: (channelId) => {
 *     console.log(`Reply tracking started for channel: ${channelId}`)
 *     initializeReplySession()
 *   },
 *   onStop: (channelId) => {
 *     console.log(`Reply tracking stopped for channel: ${channelId}`)
 *     clearReplySession()
 *   },
 *   onReplyCountIncreased: (channelId) => {
 *     console.log(`Reply count increased for channel: ${channelId}`)
 *     incrementLocalReplyCount()
 *   }
 * })
 *
 * // Clean up when component unmounts
 * return unsubscribe
 * ```
 */
export const subscribeToReplyEvents = ({
  onStart,
  onStop,
  onReplyCountIncreased,
}: {
  onStart?: Listener
  onStop?: Listener
  onReplyCountIncreased?: Listener
}): (() => void) => {
  const handleEvent = (e: Event): void => {
    const detail = e instanceof CustomEvent ? e.detail : undefined
    const channelId = typeof detail === 'string' ? detail : ''
    if (e.type === REPLY_TRACKING_START_EVENT && onStart) onStart(channelId)
    if (e.type === REPLY_TRACKING_STOP_EVENT && onStop) onStop(channelId)
    if (e.type === REPLY_COUNT_INCREASED_EVENT && onReplyCountIncreased) {
      onReplyCountIncreased(channelId)
    }
  }

  window.addEventListener(REPLY_TRACKING_START_EVENT, handleEvent)
  window.addEventListener(REPLY_TRACKING_STOP_EVENT, handleEvent)
  window.addEventListener(REPLY_COUNT_INCREASED_EVENT, handleEvent)

  return () => {
    window.removeEventListener(REPLY_TRACKING_START_EVENT, handleEvent)
    window.removeEventListener(REPLY_TRACKING_STOP_EVENT, handleEvent)
    window.removeEventListener(REPLY_COUNT_INCREASED_EVENT, handleEvent)
  }
}

export const triggerReplyTrackingStart = (channelId: string): void => {
  window.dispatchEvent(
    new CustomEvent(REPLY_TRACKING_START_EVENT, { detail: channelId }),
  )
}

export const triggerReplyTrackingStop = (channelId: string): void => {
  window.dispatchEvent(
    new CustomEvent(REPLY_TRACKING_STOP_EVENT, { detail: channelId }),
  )
}

/**
 * Triggers a reply count increased event for the specified channel.
 *
 * This function should be called whenever a user successfully replies to a comment.
 * It notifies all subscribers that the reply count has increased, allowing the
 * tracking system to update the count and potentially trigger the "RepliedToManyComments"
 * empty state when the threshold is reached.
 *
 * @param channelId - The ID of the channel where the reply was posted
 *
 * @example
 * ```typescript
 * // Call this after a successful comment reply
 * if (replyResult.success) {
 *   triggerReplyCountIncreased(comment.channelId)
 * }
 * ```
 */
export const triggerReplyCountIncreased = (channelId: string): void => {
  window.dispatchEvent(
    new CustomEvent(REPLY_COUNT_INCREASED_EVENT, { detail: channelId }),
  )
}
